import { OnboardingConfig } from 'config/onboarding'
// Case 2 of the access restrictions (ClickUp 86akmywy6): the workspace exists
// but its trial never started. A paid lead lands here, so instead of demo data
// the dashboard makes the case for starting the trial: a hero with the demo
// video, a client strip, and the first four cards blurred under the setup
// steps. Built 1:1 from canvas option B ("Light hero, steps and proof").
import {
	AppButton,
	AppIconButton,
	Modal,
	Card,
	Chip,
	IntegrationLogo,
	Skeleton,
	StatCard,
	useEnterExit,
	useInert
} from '@clickguard/design-system'
import ArrowRight from '@untitled-ui/icons-react/build/esm/ArrowRight'
import Check from '@untitled-ui/icons-react/build/esm/Check'
import Gift01 from '@untitled-ui/icons-react/build/esm/Gift01'
import Play from '@untitled-ui/icons-react/build/esm/Play'
import ShieldTick from '@untitled-ui/icons-react/build/esm/ShieldTick'
import Star01 from '@untitled-ui/icons-react/build/esm/Star01'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import { createPortal } from 'react-dom'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTrackPostHogEvent } from 'utils/posthog'
import { ReactComponent as CarmaxLogo } from 'assets/client-logos/carmax.svg'
import { ReactComponent as NordvpnLogo } from 'assets/client-logos/nordvpn.svg'
import { ReactComponent as QuickenLogo } from 'assets/client-logos/quicken.svg'
import { ReactComponent as SafeLogo } from 'assets/client-logos/safe.svg'
import { ReactComponent as StubhubLogo } from 'assets/client-logos/stubhub.svg'
import { DASHBOARD_ROWS } from './no-ad-account-dashboard'

const ENTER_MS = 200
const viewedWorkspaces = new Set<string>()

const BENEFITS = [
	'Protects against bots and low-intent traffic',
	'Enterprise-compliant with full transparency',
	'Set up in minutes, no technical expertise needed'
]

// The customer strip from clickguard.com's homepage ("+3,000 companies rely on
// ClickGuard"), copied from its CDN on 8 Oct 2026 with the navy fill swapped
// for currentColor, so the strip greys them with a text token.
const CLIENT_LOGOS = [
	{ name: 'CarMax', Logo: CarmaxLogo },
	{ name: 'NordVPN', Logo: NordvpnLogo },
	{ name: 'Quicken', Logo: QuickenLogo },
	{ name: 'StubHub', Logo: StubhubLogo },
	{ name: 'Safe', Logo: SafeLogo }
]

const SETUP_STEPS = [
	{ label: 'Choose your plan and start your 7-day trial', time: '1 minute' },
	{ label: 'Connect your ad account', time: '2 minutes' },
	{
		label: 'Install ClickGuard’s tracking code on your website',
		time: '5 minutes'
	}
]

// The faint dot grid behind the hero, over a brand tint that fades to the
// surface. Tokens only, so it follows the theme.
const HERO_TEXTURE: CSSProperties = {
	backgroundImage:
		'radial-gradient(rgb(var(--cg-brand-500) / 0.08) 1px, transparent 1.2px), linear-gradient(180deg, var(--color-brand-50) 0%, transparent 100%)',
	backgroundSize: '9px 9px, 100% 100%'
}

const VIDEO_FRAME: CSSProperties = {
	backgroundImage:
		'linear-gradient(150deg, rgb(var(--cg-brand-hover)), rgb(var(--cg-brand-dark)))'
}

// The first four cards of the live dashboard, blurred and inert behind the
// setup steps: same titles and icons, skeleton bodies, no data queries.
// Their opacity paints them like a positioned layer, so the steps card above
// them carries z-[1] to stay on top.
const GhostCards = () => {
	const ref = useRef<HTMLDivElement>(null)
	useInert(ref, true)
	const cards = DASHBOARD_ROWS.slice(0, 2).flatMap((row, rowIndex) =>
		row.cards.map((card) => ({ ...card, tall: rowIndex === 1 }))
	)
	return (
		<div
			ref={ref}
			aria-hidden
			className="col-start-1 row-start-1 grid grid-cols-1 xl:grid-cols-2 gap-3 blur-[5px] opacity-50 select-none"
		>
			{cards.map(({ title, Icon, color, tall }) => (
				<StatCard
					key={title}
					size="small"
					title={title}
					icon={
						<Icon
							className="w-4 h-4 flex-shrink-0"
							style={{ color: color ?? 'var(--color-brand)' }}
						/>
					}
					className={`w-full ${tall ? 'h-[300px]' : 'h-[240px]'}`}
				>
					<div className="flex flex-col gap-3 pt-2">
						<Skeleton className="h-3 w-3/4" />
						<Skeleton className="h-6 w-1/3" />
						<Skeleton className="h-3 w-2/3" />
					</div>
				</StatCard>
			))}
		</div>
	)
}

const TrialButton = ({
	unlocked,
	chip,
	onClick
}: {
	unlocked: boolean
	chip: { mounted: boolean; entered: boolean }
	onClick: () => void
}) => (
	<AppButton
		variant="primary"
		size="md"
		leftIcon={
			unlocked ? (
				<Gift01 width={18} height={18} />
			) : (
				<ShieldTick width={18} height={18} />
			)
		}
		label="Start free trial"
		rightIcon={
			chip.mounted ? (
				<span
					className={`inline-flex transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
						chip.entered ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
					}`}
				>
					<Chip
						size="sm"
						color="brand"
						aria-label="15% off any quarterly plan, unlocked"
						style={
							{
								'--cg-brand': '255 255 255',
								'--cg-brand-200': '110 102 253'
							} as CSSProperties
						}
					>
						15% off
					</Chip>
				</span>
			) : undefined
		}
		onClick={onClick}
	/>
)

export const NotStartedDashboard = ({
	accountId
}: {
	accountId?: string
	accountName?: string
}) => {
	const trackPostHogEvent = useTrackPostHogEvent()
	const navigate = useNavigate()
	const [videoOpen, setVideoOpen] = useState(false)
	useEffect(() => {
		if (accountId && !viewedWorkspaces.has(accountId)) {
			viewedWorkspaces.add(accountId)
			trackPostHogEvent('pageview', 'onboarding', null)
		}
	}, [accountId, trackPostHogEvent])
	const { entered } = useEnterExit(true, { duration: ENTER_MS })
	// Opening the interactive demo unlocks the quarterly offer.
	const [offerUnlocked, setOfferUnlocked] = useState(false)
	const offerLine = useEnterExit(offerUnlocked, { duration: ENTER_MS })
	const [offerGlow, setOfferGlow] = useState(false)
	useEffect(() => {
		if (!offerUnlocked) return
		setOfferGlow(true)
		const timer = window.setTimeout(() => setOfferGlow(false), 1200)
		return () => window.clearTimeout(timer)
	}, [offerUnlocked])

	const startTrial = () => {
		trackPostHogEvent('hero_cta_start_trial', 'onboarding', {
			offer: offerUnlocked
		})
		if (accountId)
			navigate(
				`/start/standard/${accountId}?step=plan-payment${
					offerUnlocked ? '&offer=quarterly' : ''
				}`
			)
	}

	return (
		<div
			className={`flex flex-col gap-4 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
				entered ? 'opacity-100' : 'opacity-0 translate-y-1'
			}`}
		>
			<section
				aria-labelledby="activation-hero-title"
				className="bg-white border border-brand-100 rounded-[22px] p-6 md:p-8 flex flex-wrap items-center gap-10"
				style={HERO_TEXTURE}
			>
				<div className="flex-[1_1_360px] min-w-0 flex flex-col gap-3.5">
					<h2
						id="activation-hero-title"
						className="m-0 font-grotesk text-[30px] md:text-[34px] leading-[1.1] font-semibold text-t-default"
					>
						<span className="text-brand">Verify</span> every click,
						<br />
						<span className="text-brand">defend</span> every dollar.
					</h2>
					<p className="m-0 max-w-[480px] text-[16px] leading-normal text-t-secondary">
						ClickGuard’s proprietary AI blocks bots, human fraud and wasteful
						clicks, so your ad budget reaches real buyers.
					</p>
					<ul className="m-0 p-0 list-none flex flex-col gap-2 text-[14px] text-t-default">
						{BENEFITS.map((benefit) => (
							<li key={benefit} className="flex items-start gap-2.5">
								<Check
									width={18}
									height={18}
									className="shrink-0 mt-px text-brand"
								/>
								{benefit}
							</li>
						))}
					</ul>
					<div className="flex flex-col items-start gap-2.5 mt-1.5">
						{/* Briefly highlight the trial button when the offer unlocks. */}
						<span
							className={`inline-flex rounded-[10px] transition-shadow duration-500 ease-out motion-reduce:transition-none ${
								offerGlow
									? 'shadow-[0_0_0_6px_rgba(85,75,253,0.25)]'
									: 'shadow-[0_0_0_0_rgba(85,75,253,0)]'
							}`}
						>
							<TrialButton
								unlocked={offerUnlocked}
								chip={offerLine}
								onClick={startTrial}
							/>
						</span>
						<span className="flex items-center gap-1.5 text-[12px] text-t-secondary">
							<span
								className="flex gap-px text-[rgb(var(--cg-brand-400))]"
								role="img"
								aria-label="Rated 4.8 out of 5"
							>
								{[0, 1, 2, 3, 4].map((i) => (
									<Star01 key={i} width={13} height={13} fill="currentColor" />
								))}
							</span>
							4.8/5 based on 366 reviews
						</span>
					</div>
					<div className="flex flex-col items-start gap-0.5 pt-3 border-t border-t-traffic">
						<p className="m-0 text-[14px] leading-relaxed text-t-secondary">
							Not ready?{' '}
							<a
								href={OnboardingConfig.trafficAuditUrl}
								target="_blank"
								rel="noopener noreferrer"
								onClick={() =>
									trackPostHogEvent('hero_cta_free_audit', 'onboarding', null)
								}
								className="font-semibold text-t-default no-underline hover:underline"
							>
								Run a free{' '}
								<IntegrationLogo
									provider="google-ads"
									size={16}
									className="inline-flex align-[-3px]"
								/>{' '}
								traffic quality audit
								<ArrowRight
									width={14}
									height={14}
									className="inline ml-1.5 align-[-2px]"
								/>
							</a>
						</p>
						<span className="text-[12px] text-t-secondary">
							No credit card required
						</span>
					</div>
				</div>

				<div className="flex-[1_1_380px] min-w-0 flex flex-col items-center gap-3">
					{/* The interactive demo opens in a modal; opening it unlocks the offer. */}
					<button
						type="button"
						aria-label="Watch a 90 second demo and unlock a special offer"
						onClick={() => {
							setOfferUnlocked(true)
							setVideoOpen(true)
							trackPostHogEvent('hero_cta_watch_demo', 'onboarding', null)
						}}
						className="w-full aspect-[16/10] flex items-center justify-center overflow-hidden rounded-[18px] border-[6px] border-white shadow-[0_24px_60px_-24px_rgba(36,22,143,0.45)] cursor-pointer"
						style={VIDEO_FRAME}
					>
						<span className="flex flex-col items-center gap-3">
							<span className="flex items-center justify-center w-16 h-16 rounded-full bg-[rgb(255,255,255)] shadow-[0_0_0_10px_rgba(255,255,255,0.14)]">
								<Play
									width={26}
									height={26}
									fill="currentColor"
									className="text-brand ml-1"
								/>
							</span>
							<span className="flex flex-col items-center gap-0.5">
								<span className="text-[16px] font-semibold text-white">
									Watch a 90 second demo
								</span>
								<span className="text-[14px] text-[rgba(255,255,255,0.8)]">
									and unlock a special offer!
								</span>
							</span>
						</span>
					</button>
					{offerLine.mounted && (
						<p
							aria-live="polite"
							className={`m-0 flex items-start gap-2 text-[14px] leading-5 text-t-default transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none ${
								offerLine.entered ? 'opacity-100' : 'opacity-0 -translate-y-1'
							}`}
						>
							{/* The same gift as on the trial buttons ties the two together. */}
							<Gift01
								width={18}
								height={18}
								aria-hidden
								className="shrink-0 mt-px text-brand"
							/>
							<span>
								Unlocked: <strong className="font-semibold">15% off</strong> any{' '}
								<strong className="font-semibold">quarterly</strong> plan.
								<br />
								Start your <strong className="font-semibold">
									free trial
								</strong>{' '}
								to claim it.
							</span>
						</p>
					)}
				</div>
			</section>

			<div className="flex flex-wrap items-center gap-x-7 gap-y-3 px-1 py-1.5">
				<span className="shrink-0 text-[13px] text-t-secondary">
					Trusted by 3,000+ advertisers worldwide
				</span>
				{CLIENT_LOGOS.map(({ name, Logo }) => (
					<span
						key={name}
						className="flex-[1_1_100px] h-9 flex items-center justify-center text-[rgb(var(--cg-text-tertiary))]"
					>
						<Logo role="img" aria-label={name} className="h-9 w-auto" />
					</span>
				))}
			</div>

			<div className="grid">
				<GhostCards />
				<Card
					radius="xl"
					padding="loose"
					shadow="sm"
					className="relative z-[1] col-start-1 row-start-1 self-center justify-self-center w-[min(560px,calc(100%-32px))] shadow-[0_24px_60px_-28px_rgba(17,21,83,0.35)]"
				>
					<section
						aria-labelledby="activation-steps-title"
						className="flex flex-col gap-1"
					>
						<h3
							id="activation-steps-title"
							className="m-0 mb-1 flex items-center gap-2.5 text-[18px] font-semibold text-t-default"
						>
							<ShieldTick width={20} height={20} />
							Your website fully protected, in minutes
						</h3>
						<p className="m-0 mb-3 text-[14px] text-t-secondary">
							Here’s how it will work:
						</p>
						<ol className="m-0 !p-0 list-none flex flex-col">
							{SETUP_STEPS.map((step, index) => (
								<li
									key={step.label}
									className="flex items-center gap-3 py-[11px] border-b border-t-traffic"
								>
									<span className="flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-brand-50 text-brand text-[12px] font-semibold">
										{index + 1}
									</span>
									<span className="flex-1 text-[14px] text-t-default">
										{step.label}
									</span>
									<Chip size="sm" color="brand">
										{step.time}
									</Chip>
								</li>
							))}
							<li className="flex items-center gap-3 py-[11px]">
								{/* DS lemon-bg, the "done" accent. */}
								<span className="flex shrink-0 items-center justify-center w-6 h-6 rounded-full bg-[rgb(238,255,157)] text-on-lemon text-[12px] font-semibold">
									4
								</span>
								<span className="flex-1 text-[14px] font-medium text-t-default">
									You’re all set
								</span>
							</li>
						</ol>
						<div className="mt-3.5">
							{/* Share the hero button’s offer state and checkout destination. */}
							<TrialButton
								unlocked={offerUnlocked}
								chip={offerLine}
								onClick={startTrial}
							/>
						</div>
					</section>
				</Card>
			</div>
			{videoOpen &&
				createPortal(
					<Modal
						open
						onClose={() => setVideoOpen(false)}
						size="xl"
						rounded="2xl"
						className="!w-[min(1120px,calc(100vw-2rem),calc(160dvh-10rem))] overflow-hidden border border-border-light !shadow-2xl"
						overlayClassName="!bg-slate-950/70 backdrop-blur-sm"
					>
						<div className="flex items-center justify-between gap-4 px-4 py-3 sm:px-6 sm:py-4">
							<div className="min-w-0">
								<h2 className="m-0 text-base sm:text-lg font-semibold text-t-default">
									Explore ClickGuard
								</h2>
								<p className="m-0 mt-1 text-sm text-t-secondary">
									Take an interactive tour of your dashboard.
								</p>
							</div>
							<AppIconButton
								variant="ghost"
								size="md"
								icon={<XClose />}
								aria-label="Close interactive demo"
								autoFocus
								onClick={() => setVideoOpen(false)}
								className="shrink-0"
							/>
						</div>
						<div className="bg-slate-950 p-2 sm:p-3">
							<iframe
								title="ClickGuard interactive demo"
								src={OnboardingConfig.productTourUrl}
								className="block w-full aspect-video rounded-lg border-0"
								allowFullScreen
							/>
						</div>
					</Modal>,
					document.body
				)}
		</div>
	)
}
